import { proxy } from "valtio";
import type { ThemeColors, ThemeConfig } from "@/lib/theme/types";
import { defaultTheme } from "@/lib/theme/defaults";
import {
  applyTheme,
  downloadTheme,
  loadThemeFromFile,
} from "@/lib/theme/utils";

const STORAGE_KEY = "vizi-theme-editor";

/**
 * Theme editor store state
 */
interface ThemeEditorStore {
  /** Whether the theme editor panel is open */
  isOpen: boolean;
  /** Panel width percentage (for resizable panel) */
  panelSize: number;
  /** Currently editing light or dark mode */
  editingMode: "light" | "dark";
  /** Current theme being edited */
  currentTheme: ThemeConfig;
  /** Whether there are unsaved changes */
  hasChanges: boolean;
}

/**
 * Load persisted theme editor state from localStorage
 */
function loadPersistedState(): Partial<ThemeEditorStore> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

/**
 * Persist theme editor state to localStorage
 */
function persistState(state: Partial<ThemeEditorStore>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

const persisted = loadPersistedState();

// Ensure panel size is within reasonable bounds (25-80%)
const MIN_PANEL_SIZE = 25;
const MAX_PANEL_SIZE = 80;
const DEFAULT_PANEL_SIZE = 40;

function clampPanelSize(size: number | undefined): number {
  if (size === undefined || isNaN(size) || size < MIN_PANEL_SIZE) {
    return DEFAULT_PANEL_SIZE;
  }
  return Math.min(MAX_PANEL_SIZE, size);
}

/**
 * Theme editor Valtio store
 */
export const themeEditorStore = proxy<ThemeEditorStore>({
  isOpen: false, // Always start closed
  panelSize: clampPanelSize(persisted.panelSize),
  editingMode: persisted.editingMode ?? "light",
  currentTheme: persisted.currentTheme ?? { ...defaultTheme },
  hasChanges: false,
});

/**
 * Apply the current theme to the document
 * Called whenever theme changes for live preview
 */
function applyCurrentTheme() {
  const isDark = document.documentElement.classList.contains("dark");
  applyTheme(themeEditorStore.currentTheme, isDark);
}

/**
 * Persist all settings
 */
function persistAllSettings() {
  persistState({
    panelSize: themeEditorStore.panelSize,
    editingMode: themeEditorStore.editingMode,
    currentTheme: themeEditorStore.currentTheme,
  });
}

// --- Actions ---

/**
 * Open the theme editor panel
 */
export function openThemeEditor(): void {
  themeEditorStore.isOpen = true;
  applyCurrentTheme();
}

/**
 * Close the theme editor panel
 */
export function closeThemeEditor(): void {
  themeEditorStore.isOpen = false;
}

/**
 * Set the panel size (called during resize)
 */
export function setPanelSize(size: number): void {
  themeEditorStore.panelSize = clampPanelSize(size);
  persistAllSettings();
}

/**
 * Set the editing mode (light or dark)
 */
export function setEditingMode(mode: "light" | "dark"): void {
  themeEditorStore.editingMode = mode;
  persistAllSettings();
}

/**
 * Update a single color in the current theme
 */
export function updateColor(key: keyof ThemeColors, value: string): void {
  const mode = themeEditorStore.editingMode;
  themeEditorStore.currentTheme[mode][key] = value;
  themeEditorStore.hasChanges = true;

  // Apply immediately for live preview
  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Update the border radius
 */
export function updateRadius(radius: string): void {
  themeEditorStore.currentTheme.radius = radius;
  themeEditorStore.hasChanges = true;

  // Apply theme to rebuild CSS with new radius
  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Update theme name and display name
 */
export function updateThemeName(name: string, displayName: string): void {
  themeEditorStore.currentTheme.name = name;
  themeEditorStore.currentTheme.displayName = displayName;
  themeEditorStore.hasChanges = true;
  persistAllSettings();
}

/**
 * Apply a preset theme
 */
export function applyPreset(preset: ThemeConfig): void {
  themeEditorStore.currentTheme = { ...preset };
  themeEditorStore.hasChanges = true;

  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Reset to default theme
 */
export function resetToDefault(): void {
  themeEditorStore.currentTheme = { ...defaultTheme };
  themeEditorStore.hasChanges = false;

  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Generate a random theme with harmonious colors
 */
export function randomizeTheme(): void {
  // Pick a random primary hue (0-360)
  const primaryHue = Math.floor(Math.random() * 360);

  // Generate complementary/analogous hues
  const secondaryHue = (primaryHue + 30 + Math.random() * 30) % 360;
  const accentHue = (primaryHue + 180 + Math.random() * 60 - 30) % 360;
  const destructiveHue = 0 + Math.random() * 20; // Keep destructive red-ish

  // Random saturation variations (60-90%)
  const primarySat = 60 + Math.floor(Math.random() * 30);
  const secondarySat = 20 + Math.floor(Math.random() * 30);

  // Random radius
  const radiusOptions = ["0", "0.3rem", "0.5rem", "0.75rem", "1rem"];
  const radius =
    radiusOptions[Math.floor(Math.random() * radiusOptions.length)];

  // Chart colors - spread across the color wheel
  const chartHues = [
    primaryHue,
    (primaryHue + 72) % 360,
    (primaryHue + 144) % 360,
    (primaryHue + 216) % 360,
    (primaryHue + 288) % 360,
  ];

  // Background variations - dramatic colors for UI designer experimentation
  const useTintedBackground = Math.random() > 0.15; // 85% chance of tinted background

  // Background hue - 60% uses primary hue, 40% uses independent random hue
  const useIndependentBgHue = Math.random() > 0.6;
  const bgHue = useIndependentBgHue
    ? Math.floor(Math.random() * 360)
    : primaryHue;

  // Light mode background: noticeable tint with good variation
  const lightBgSat = useTintedBackground
    ? Math.floor(15 + Math.random() * 35)
    : 0; // 15-50% or 0
  const lightBgLight = 92 + Math.floor(Math.random() * 6); // 92-97%
  const lightBackground = useTintedBackground
    ? `${bgHue} ${lightBgSat}% ${lightBgLight}%`
    : "0 0% 100%";

  // Dark mode background: richer tint with more depth
  const darkBgSat = useTintedBackground
    ? Math.floor(25 + Math.random() * 35)
    : 0; // 25-60% or 0
  const darkBgLight = 6 + Math.floor(Math.random() * 8); // 6-13%
  const darkBackground = useTintedBackground
    ? `${bgHue} ${darkBgSat}% ${darkBgLight}%`
    : `0 0% ${darkBgLight}%`;

  // Card colors - slightly different from background for depth
  const lightCardLight = Math.min(99, lightBgLight + 2);
  const lightCard = useTintedBackground
    ? `${bgHue} ${Math.max(0, lightBgSat - 5)}% ${lightCardLight}%`
    : "0 0% 100%";

  const darkCardLight = darkBgLight + 3;
  const darkCard = useTintedBackground
    ? `${bgHue} ${Math.max(0, darkBgSat - 8)}% ${darkCardLight}%`
    : `0 0% ${darkCardLight}%`;

  const randomTheme: ThemeConfig = {
    name: "random",
    displayName: "Random",
    radius,
    light: {
      background: lightBackground,
      foreground: `${primaryHue} 10% 10%`,
      card: lightCard,
      "card-foreground": `${primaryHue} 10% 10%`,
      popover: lightCard,
      "popover-foreground": `${primaryHue} 10% 10%`,
      primary: `${primaryHue} ${primarySat}% 50%`,
      "primary-foreground": "0 0% 100%",
      secondary: `${secondaryHue} ${secondarySat}% 95%`,
      "secondary-foreground": `${secondaryHue} ${secondarySat}% 20%`,
      muted: `${primaryHue} 10% 95%`,
      "muted-foreground": `${primaryHue} 10% 45%`,
      accent: `${accentHue} 50% 95%`,
      "accent-foreground": `${accentHue} 50% 20%`,
      destructive: `${destructiveHue} 85% 55%`,
      "destructive-foreground": "0 0% 100%",
      border: `${primaryHue} 15% 90%`,
      input: `${primaryHue} 15% 90%`,
      ring: `${primaryHue} ${primarySat}% 50%`,
      "chart-1": `${chartHues[0]} 70% 55%`,
      "chart-2": `${chartHues[1]} 60% 45%`,
      "chart-3": `${chartHues[2]} 50% 35%`,
      "chart-4": `${chartHues[3]} 65% 60%`,
      "chart-5": `${chartHues[4]} 75% 50%`,
    },
    dark: {
      background: darkBackground,
      foreground: `${primaryHue} 10% 95%`,
      card: darkCard,
      "card-foreground": `${primaryHue} 10% 95%`,
      popover: darkCard,
      "popover-foreground": `${primaryHue} 10% 95%`,
      primary: `${primaryHue} ${primarySat}% 55%`,
      "primary-foreground": `${primaryHue} 20% 10%`,
      secondary: `${secondaryHue} ${secondarySat}% 15%`,
      "secondary-foreground": `${secondaryHue} 10% 90%`,
      muted: `${primaryHue} 15% 15%`,
      "muted-foreground": `${primaryHue} 10% 60%`,
      accent: `${accentHue} 40% 15%`,
      "accent-foreground": `${accentHue} 40% 90%`,
      destructive: `${destructiveHue} 70% 45%`,
      "destructive-foreground": "0 0% 95%",
      border: `${primaryHue} 15% 15%`,
      input: `${primaryHue} 15% 15%`,
      ring: `${primaryHue} ${primarySat}% 55%`,
      "chart-1": `${chartHues[0]} 65% 50%`,
      "chart-2": `${chartHues[1]} 55% 55%`,
      "chart-3": `${chartHues[2]} 45% 45%`,
      "chart-4": `${chartHues[3]} 60% 55%`,
      "chart-5": `${chartHues[4]} 70% 45%`,
    },
  };

  themeEditorStore.currentTheme = randomTheme;
  themeEditorStore.hasChanges = true;

  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Export current theme as JSON file download
 */
export function exportCurrentTheme(): void {
  downloadTheme(themeEditorStore.currentTheme);
}

/**
 * Import a theme from a file
 */
export async function importTheme(file: File): Promise<void> {
  const theme = await loadThemeFromFile(file);
  themeEditorStore.currentTheme = theme;
  themeEditorStore.hasChanges = true;

  applyCurrentTheme();
  persistAllSettings();
}

/**
 * Initialize theme editor - apply persisted theme on app load
 * Call this from main.tsx or App.tsx
 */
export function initializeThemeEditor(): void {
  // Only apply if there's a persisted theme with changes
  if (persisted.currentTheme) {
    const isDark = document.documentElement.classList.contains("dark");
    applyTheme(themeEditorStore.currentTheme, isDark);
  }

  // Watch for dark mode changes and re-apply theme
  // This syncs theme colors when user toggles light/dark in settings
  const observer = new MutationObserver(() => {
    if (persisted.currentTheme) {
      applyCurrentTheme();
    }
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
}
